import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const SearchExecutionContext = createContext();

export const useSearchExecution = () => {
  const context = useContext(SearchExecutionContext);
  if (!context) {
    throw new Error('useSearchExecution must be used within a SearchExecutionProvider');
  }
  return context;
};

export const SearchExecutionProvider = ({ children }) => {
  const [executionState, setExecutionState] = useState(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem('searchExecutionState');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Check if the execution is still valid (not older than 1 hour)
        const startTime = new Date(parsed.startTime);
        const now = new Date();
        const hoursSinceStart = (now - startTime) / (1000 * 60 * 60);
        
        if (hoursSinceStart < 1 && parsed.status === 'running') {
          return parsed;
        }
      } catch (e) {
        console.error('Failed to parse stored execution state:', e);
      }
    }
    return {
      isRunning: false,
      batchId: null,
      batchName: null,
      status: null,
      startTime: null
    };
  });

  // Persist state to localStorage whenever it changes
  useEffect(() => {
    if (executionState.isRunning) {
      localStorage.setItem('searchExecutionState', JSON.stringify(executionState));
    } else {
      localStorage.removeItem('searchExecutionState');
    }
  }, [executionState]);

  const startExecution = useCallback((batchId, batchName) => {
    const newState = {
      isRunning: true,
      batchId,
      batchName,
      status: 'running',
      startTime: new Date().toISOString()
    };
    setExecutionState(newState);
    
    // Also store in sessionStorage for quick access
    sessionStorage.setItem('currentBatchId', batchId);
  }, []);

  const updateExecutionStatus = useCallback((status) => {
    setExecutionState(prev => ({
      ...prev,
      status
    }));
  }, []);

  const completeExecution = useCallback((status = 'completed') => {
    setExecutionState({
      isRunning: false,
      batchId: null,
      batchName: null,
      status: null,
      startTime: null
    });
    
    // Clear sessionStorage
    sessionStorage.removeItem('currentBatchId');
  }, []);

  const isExecuting = useCallback(() => {
    return executionState.isRunning;
  }, [executionState.isRunning]);

  const getCurrentBatchId = useCallback(() => {
    return executionState.batchId;
  }, [executionState.batchId]);

  // Check for stale executions on mount
  useEffect(() => {
    if (executionState.isRunning && executionState.startTime) {
      const startTime = new Date(executionState.startTime);
      const now = new Date();
      const hoursSinceStart = (now - startTime) / (1000 * 60 * 60);
      
      // If execution has been running for more than 1 hour, assume it's stale
      if (hoursSinceStart >= 1) {
        console.log('Clearing stale execution state');
        completeExecution('timeout');
      }
    }
  }, []); // Only run on mount

  // Listen for storage events to sync across tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'searchExecutionState') {
        if (e.newValue) {
          try {
            const newState = JSON.parse(e.newValue);
            setExecutionState(newState);
          } catch (error) {
            console.error('Failed to parse storage event:', error);
          }
        } else {
          // Storage was cleared
          setExecutionState({
            isRunning: false,
            batchId: null,
            batchName: null,
            status: null,
            startTime: null
          });
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const value = {
    executionState,
    startExecution,
    updateExecutionStatus,
    completeExecution,
    isExecuting,
    getCurrentBatchId
  };

  return (
    <SearchExecutionContext.Provider value={value}>
      {children}
    </SearchExecutionContext.Provider>
  );
};